// src/components/account/LoginClient.tsx
"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import SafeHtml from "@/components/ui/SafeHtml";
import OtpInput from "@/components/ui/OtpInput";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import { useLayoutData } from "../layout/LayoutDataProvider";
import { apiPath } from "@/lib/apiPath";

const OTP_LEN = 6;
const RESEND_COOLDOWN_SECONDS = 20;

export default function LoginClient() {
  const router = useRouter();

  const { templateRoutes, global_text } = useLayoutData();
  const routes = templateRoutes ?? {};

  const accountHref = routes["page-account.php"];
  const signupHref = routes["page-signup.php"];
  const forgotHref = routes["page-forgot-password.php"];

  const LOGIN: Record<string, string> =
    (global_text as { auth?: { login?: Record<string, string> } } | undefined)
      ?.auth?.login ?? {};
  const t = (k: string, fallback = "") => LOGIN[k] || fallback;

  const [step, setStep] = React.useState<"creds" | "code">("creds");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [challengeId, setChallengeId] = React.useState("");
  const [otp, setOtp] = React.useState<string[]>(Array(OTP_LEN).fill(""));

  const [loading, setLoading] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);

  const [topMessage, setTopMessage] = React.useState<string | null>(null);
  const [topMessageType, setTopMessageType] = React.useState<"success" | "error" | null>(null);

  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [codeError, setCodeError] = React.useState<string | null>(null);

  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const passwordRef = React.useRef<HTMLInputElement | null>(null);

  const [resendLeft, setResendLeft] = React.useState(0);
  const resendTimerRef = React.useRef<number | null>(null);

  const busy = loading || redirecting;

  React.useEffect(() => {
    if (emailError) emailRef.current?.focus?.();
    else if (passwordError) passwordRef.current?.focus?.();
  }, [emailError, passwordError]);

  React.useEffect(() => {
    return () => {
      if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    };
  }, []);

  function startCooldown(seconds = RESEND_COOLDOWN_SECONDS) {
    if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
    resendTimerRef.current = null;
    setResendLeft(seconds);
    resendTimerRef.current = window.setInterval(() => {
      setResendLeft((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) window.clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function resetMessages() {
    setTopMessage(null);
    setTopMessageType(null);
    setEmailError(null);
    setPasswordError(null);
    setCodeError(null);
  }

  function clearOtp() {
    setOtp(Array(OTP_LEN).fill(""));
  }

  function getOtpCode() {
    return otp.join("");
  }

  async function startLoginFlow() {
    const res = await fetch(apiPath("/api/auth/login/start"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json().catch(() => ({}));
    return { res, json };
  }

  async function start(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    if (!email.trim()) setEmailError(t("error_email_required", "Please enter your email."));
    if (!password) setPasswordError(t("error_password_required", "Please enter your password."));
    if (!email.trim() || !password) return;

    setLoading(true);
    try {
      const { res, json } = await startLoginFlow();

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_login_failed", "Sign in failed."));
        setLoading(false);
        return;
      }

      setChallengeId(json.challengeId);
      clearOtp();
      setStep("code");
      setTopMessageType("success");
      startCooldown();
      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function resendCode() {
    resetMessages();
    if (resendLeft > 0) return;

    if (!email.trim() || !password) {
      if (!email.trim()) setEmailError(t("error_email_required", "Please enter your email."));
      if (!password) setPasswordError(t("error_password_required", "Please enter your password."));
      setStep("creds");
      return;
    }

    setLoading(true);
    try {
      const { res, json } = await startLoginFlow();

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_resend_failed", "Could not resend code."));
        setLoading(false);
        return;
      }

      setChallengeId(json.challengeId);
      clearOtp();
      setStep("code");
      setTopMessageType("success");
      setTopMessage(t("resend_success", "Code resent. Check your inbox."));
      startCooldown();
      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();

    const code = getOtpCode().replace(/\D/g, "");
    if (code.length !== OTP_LEN || otp.some((d) => d === "")) {
      setCodeError(t("error_code_incomplete", "Enter the 6-digit code."));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiPath("/api/auth/login/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        credentials: "include",
        body: JSON.stringify({ challengeId, code }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setTopMessageType("error");
        setTopMessage(json?.message ?? t("error_verify_failed", "Verification failed."));
        setLoading(false);
        return;
      }

      if (accountHref) {
        setRedirecting(true);
        router.replace(accountHref);
        router.refresh();
        return;
      }

      setLoading(false);
    } catch {
      setTopMessageType("error");
      setTopMessage(t("error_network", "Network error. Please try again."));
      setLoading(false);
    }
  }

  const resendBase = t("resend_button", "Resend code");
  const resendLabel = resendLeft > 0 ? `${resendBase} (${resendLeft}s)` : resendBase;

  return (
    <Section>
      <Wrapper>
        <div className="mx-auto max-w-lg">
          <div className="flex flex-col gap-6">
            <h1 className="text-title-1 font-semibold">
              {t("heading", "Sign in")}
            </h1>

            {step === "creds" ? (
              <form onSubmit={start} noValidate className="flex flex-col gap-6">
                <div className="w-full min-w-0">
                  <Input
                    id="login-email"
                    ref={emailRef}
                    label={<SafeHtml content={t("email_label", "Email")} />}
                    type="email"
                    value={email}
                    onChange={(e) => { setEmailError(null); setEmail(e.target.value); }}
                    autoComplete="email"
                    aria-invalid={!!emailError || undefined}
                    aria-describedby={emailError ? "login-email-error" : undefined}
                    invalid={!!emailError}
                    required
                  />
                  {emailError && (
                    <small id="login-email-error" role="alert" className="mt-1 block text-body-3 text-fuji-red">
                      {emailError}
                    </small>
                  )}
                </div>

                <div className="w-full min-w-0">
                  <Input
                    id="login-password"
                    ref={passwordRef}
                    label={<SafeHtml content={t("password_label", "Password")} />}
                    type="password"
                    value={password}
                    onChange={(e) => { setPasswordError(null); setPassword(e.target.value); }}
                    autoComplete="current-password"
                    aria-invalid={!!passwordError || undefined}
                    aria-describedby={passwordError ? "login-password-error" : undefined}
                    invalid={!!passwordError}
                    required
                  />
                  {passwordError && (
                    <small id="login-password-error" role="alert" className="mt-1 block text-body-3 text-fuji-red">
                      {passwordError}
                    </small>
                  )}
                </div>

                {topMessage && (
                  <p className={`m-0 ${topMessageType === "error" ? "text-fuji-red" : ""}`}>
                    {topMessage}
                  </p>
                )}

                <div className="flex w-full flex-col gap-3">
                  <Button type="submit" className="group w-full" disabled={busy} loading={busy} center>
                    <SafeHtml content={t("submit_button", "Sign in")} />
                  </Button>

                  <div className="flex items-center justify-between text-body-2">
                    {forgotHref && (
                      <Link className="underline" href={forgotHref}>
                        {t("forgot_link", "Forgot password?")}
                      </Link>
                    )}
                    {signupHref && (
                      <Link className="underline" href={signupHref}>
                        {t("signup_link", "Create account")}
                      </Link>
                    )}
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={verify} noValidate className="flex flex-col gap-6">
                <p className="m-0 text-body-2">
                  <SafeHtml
                    content={t("otp_sent_to_html", "Enter the 6-digit code sent to <strong>{email}</strong>.").replace("{email}", email)}
                  />
                </p>

                <OtpInput
                  idPrefix="login-otp"
                  label={t("otp_label", "Verification code")}
                  value={otp}
                  onChange={setOtp}
                  invalid={!!codeError}
                  error={codeError}
                  onClearError={() => setCodeError(null)}
                />

                {topMessage && (
                  <p className={`m-0 ${topMessageType === "error" ? "text-fuji-red" : ""}`}>
                    {topMessage}
                  </p>
                )}

                <div className="flex w-full flex-col gap-3">
                  <Button type="submit" className="group w-full" disabled={busy} loading={busy} center>
                    <SafeHtml content={t("verify_button", "Verify")} />
                  </Button>

                  <div className="flex items-center justify-between text-body-2">
                    <button
                      type="button"
                      className={`underline ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
                      onClick={() => {
                        if (busy) return;
                        resetMessages();
                        clearOtp();
                        setStep("creds");
                      }}
                      disabled={busy}
                    >
                      {t("back_button", "Back")}
                    </button>

                    <button
                      type="button"
                      className={`${busy || resendLeft > 0 ? "opacity-60 cursor-not-allowed" : "underline"}`}
                      onClick={resendCode}
                      disabled={busy || resendLeft > 0}
                    >
                      {resendLabel}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}